// =============================================
// useRedirectProposals — hook dla Redirect Mode (STOP-5)
//
// State: skan propozycji per node + pending redirects z DB.
// Akcje: scanForNode (quick/deep), applyPending (INSERT wp_redirects), fetchPending.
// =============================================

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  getMatchProposalsForNode,
  type MatchProposal,
  type MatchProposalMode,
  type MatchProposalsResult,
} from "@/services/existingPostMatcher";

export interface PendingRedirect {
  id: string;
  from_url: string;
  from_wp_post_id: string | null;
  to_node_id: string | null;
  to_slug_planned: string | null;
  to_wp_post_url: string | null;
  status: string;
  match_confidence: number | null;
  match_method: string | null;
  redirect_type: string;
  created_at: string | null;
}

export interface ApplyRedirectInput {
  fromUrl: string;
  fromWpPostId: string;
  toNodeId: string;
  toSlugPlanned: string;
  matchConfidence: number;
  matchMethod: string;
  reason?: string;
}

export interface NodeForScan {
  id: string;
  title: string;
  slug: string;
}

export function useRedirectProposals(mapId: string, websiteId: string | null) {
  const { toast } = useToast();
  const [scanning, setScanning] = useState(false);
  const [scanMode, setScanMode] = useState<MatchProposalMode>("quick");
  const [results, setResults] = useState<MatchProposalsResult[]>([]);
  const [totalLlmCostUsd, setTotalLlmCostUsd] = useState(0);
  const [scanDurationMs, setScanDurationMs] = useState(0);

  const [pendingRedirects, setPendingRedirects] = useState<PendingRedirect[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);

  const [eligibleNodes, setEligibleNodes] = useState<NodeForScan[]>([]);
  const [loadingNodes, setLoadingNodes] = useState(false);

  const fetchEligibleNodes = useCallback(async () => {
    // Guard: nie odpalaj fetch gdy mapId pusty / undefined (zapobiega
    // 'eq("map_id", "undefined")' → Postgres UUID syntax error).
    if (!mapId) {
      setEligibleNodes([]);
      return;
    }
    setLoadingNodes(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("thematic_nodes")
        .select("id, title, slug")
        .eq("map_id", mapId)
        .not("slug", "is", null);
      if (error) throw new Error(error.message);
      setEligibleNodes(
        ((data ?? []) as NodeForScan[]).filter((n) => n.slug && n.slug.trim().length > 0),
      );
    } catch (err) {
      toast({
        title: "Błąd pobierania nodów",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setLoadingNodes(false);
    }
  }, [mapId, toast]);

  useEffect(() => {
    fetchEligibleNodes();
  }, [fetchEligibleNodes]);

  const fetchPending = useCallback(async () => {
    if (!websiteId) {
      setPendingRedirects([]);
      return;
    }
    setLoadingPending(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("wp_redirects")
        .select(
          "id, from_url, from_wp_post_id, to_node_id, to_slug_planned, to_wp_post_url, status, match_confidence, match_method, redirect_type, created_at",
        )
        .eq("website_id", websiteId)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      setPendingRedirects((data ?? []) as PendingRedirect[]);
    } catch (err) {
      toast({
        title: "Błąd pobierania redirects",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setLoadingPending(false);
    }
  }, [websiteId, toast]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const scanAllNodes = useCallback(
    async (nodeIds: string[], mode: MatchProposalMode = "quick") => {
      if (!websiteId || nodeIds.length === 0) return;
      setScanning(true);
      setScanMode(mode);
      const startMs = Date.now();
      let totalCost = 0;
      const collected: MatchProposalsResult[] = [];
      try {
        for (const nodeId of nodeIds) {
          const res = await getMatchProposalsForNode(nodeId, mapId, { mode });
          collected.push(res);
          totalCost += res.llmCostUsd;
        }
        setResults(collected);
        setTotalLlmCostUsd(totalCost);
        setScanDurationMs(Date.now() - startMs);
        toast({
          title: "Skan zakończony",
          description: `${collected.length} nodów przeskanowanych${mode === "deep" ? ` · LLM cost $${totalCost.toFixed(4)}` : " · $0 (Quick mode)"}`,
        });
      } catch (err) {
        toast({
          title: "Błąd skanowania",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      } finally {
        setScanning(false);
      }
    },
    [mapId, websiteId, toast],
  );

  const applyPending = useCallback(
    async (input: ApplyRedirectInput) => {
      if (!websiteId) {
        toast({
          title: "Brak website_id",
          description: "Mapa nie ma powiązanej domeny.",
          variant: "destructive",
        });
        return false;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: session } = await (supabase as any).auth.getSession();
        const userId = session?.session?.user?.id;
        if (!userId) throw new Error("Nie zalogowany");

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any).from("wp_redirects").insert({
          user_id: userId,
          website_id: websiteId,
          from_url: input.fromUrl,
          from_wp_post_id: input.fromWpPostId,
          to_node_id: input.toNodeId,
          to_slug_planned: input.toSlugPlanned,
          to_wp_post_url: null, // set po publish target (STOP-6 future)
          status: "pending",
          status_code: 301,
          redirect_type: "redirect_mode_proposed",
          match_confidence: input.matchConfidence,
          match_method: input.matchMethod,
          reason: input.reason ?? null,
        });
        if (error) throw new Error(error.message);
        await fetchPending();
        toast({
          title: "Dodano do pending",
          description: `${input.fromUrl} → /${input.toSlugPlanned}`,
        });
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        toast({
          title: "Błąd Apply",
          description: msg.includes("duplicate") ? "Redirect z tego URL już istnieje" : msg,
          variant: "destructive",
        });
        return false;
      }
    },
    [websiteId, fetchPending, toast],
  );

  const deletePending = useCallback(
    async (redirectId: string) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from("wp_redirects")
          .delete()
          .eq("id", redirectId);
        if (error) throw new Error(error.message);
        await fetchPending();
        toast({ title: "Redirect usunięty", description: "Status: revoked." });
      } catch (err) {
        toast({
          title: "Błąd usuwania",
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      }
    },
    [fetchPending, toast],
  );

  return {
    scanning,
    scanMode,
    results,
    totalLlmCostUsd,
    scanDurationMs,
    pendingRedirects,
    loadingPending,
    eligibleNodes,
    loadingNodes,
    scanAllNodes,
    applyPending,
    deletePending,
    refresh: fetchPending,
    refreshNodes: fetchEligibleNodes,
  };
}

export type { MatchProposal };
